import { useCallback } from 'react';
import type { Command } from '../prompt/commands';
import { executeCommands, type ExecutionResult, type WorkspaceTab } from '../prompt/executor';
import { useProject } from './projectContext';
import { useServices } from './services';

/** Runs commands through the shared executor (same contract for prompts and buttons). */
export function useCommandRunner(navigate: (tab: WorkspaceTab) => void): (commands: Command[]) => Promise<ExecutionResult> {
  const { session, update } = useProject();
  const { audio, animation, registry, exporter, selection, motion, sync, syncSelection } = useServices();
  return useCallback(
    (commands: Command[]) =>
      executeCommands(commands, {
        getProject: () => session.snapshot.project!,
        update,
        audio,
        animation,
        navigate,
        registry,
        exportWav: () => exporter.export(session.snapshot.project!),
        selection: { ...selection.get(), frame: animation.clock.frame() },
        selectClip: (clipId) => selection.selectClip(clipId),
        selectScene: (patch) => {
          if (patch.objectIds) selection.selectObjects(patch.objectIds);
          if (patch.characterId !== undefined) selection.selectCharacter(patch.characterId);
          if (patch.layerId !== undefined) selection.selectLayer(patch.layerId);
          if (patch.animationId !== undefined) selection.selectAnimation(patch.animationId);
        },
        motion: {
          draft: () => motion.draft(),
          path: () => motion.get().path,
          propose: (draft) => motion.propose(draft),
          applied: () => motion.update({ preview: false }),
        },
        sync: {
          selection: { sessionId: sync.state.get().sessionId ?? syncSelection.get().sessionId, mappingId: syncSelection.get().mappingId },
          select: (patch) => {
            if (patch.sessionId !== undefined && patch.sessionId !== syncSelection.get().sessionId) syncSelection.selectSession(patch.sessionId);
            if (patch.mappingId !== undefined) syncSelection.update({ mappingId: patch.mappingId });
          },
          play: () => {
            // The controller follows the project after React commits; load the committed project now.
            const project = session.snapshot.project!;
            const id = syncSelection.get().sessionId ?? project.avSessions[0]?.id ?? null;
            if (!id) throw new Error('There is no sync session yet. Say “synchronize this animation with the phonk track” first.');
            sync.load(project, id);
            return sync.play();
          },
          pause: () => sync.pause(),
        },
      }),
    [session, update, audio, animation, navigate, registry, exporter, selection, motion, sync, syncSelection],
  );
}
